
import React from "react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { emphasize, styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import { useContext, useEffect, useState } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {
  deleteData,
  deleteImages,
  editData,
  fetchDataFromApi,
  uploadImage,
} from "../../utils/api";
import { useNavigate } from "react-router-dom";
import { FaRegImages } from "react-icons/fa";
import { MyContext } from "../../App";
import { useParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { IoCloseSharp } from "react-icons/io5";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import * as MuiIcons from "@mui/icons-material";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

// Enhanced auto-icon mapping with keyword extraction
const categoryIconMap = {
  // Food & Groceries
  vegetable: "Grass",
  fruit: "Apple",
  flower: "LocalFlorist",
  leaf: "Yard",
  plant: "Grass",
  meat: "SetMeal",
  chicken: "Restaurant",
  fish: "SetMeal",
  egg: "Egg",
  dairy: "Icecream",
  milk: "LocalDrink",
  bread: "BakeryDining",
  bakery: "Bakery",
  atta: "Grain",
  rice: "RiceBowl",
  dal: "SoupKitchen",
  oil: "Opacity",
  ghee: "Opacity",
  spice: "Grass",
  honey: "LocalDining",
  biscuit: "Cookie",
  cookie: "Cookie",
  snack: "Fastfood",
  meal: "Restaurant",
  food: "Restaurant",
  "dry fruit": "Grain",
  nut: "Grain",
  tea: "EmojiFoodBeverage",
  coffee: "LocalCafe",
  chocolate: "Cookie",
  "ice cream": "Icecream",
  frozen: "AcUnit",
  drink: "LocalBar",
  juice: "Liquor",
  beverage: "LocalDrink",
  water: "WaterDrop",
  soda: "LocalBar",
  cold: "AcUnit",
  pizza: "LocalPizza",
  burger: "Fastfood",
  cake: "Cake",
  sweet: "Cake",
  dessert: "Icecream",
  ramen: "RamenDining",
  noodle: "RamenDining",
  soup: "SoupKitchen",
  pasta: "DinnerDining",
  lunch: "LunchDining",
  dinner: "DinnerDining",
  breakfast: "FreeBreakfast",

  // Household & Essentials
  clean: "CleaningServices",
  cleaner: "CleaningServices",
  detergent: "CleaningServices",
  soap: "Soap",
  wash: "LocalLaundryService",
  bath: "Bathtub",
  shower: "Shower",
  shop: "LocalMall",
  store: "Store",
  grocery: "LocalGroceryStore",
  stationery: "Create",
  pen: "Edit",
  paper: "Description",
  book: "MenuBook",
  puja: "TempleBuddhist",
  temple: "TempleBuddhist",
  prayer: "TempleBuddhist",
  samagri: "TempleBuddhist",
  home: "Home",
  house: "HomeWork",
  kitchen: "Kitchen",
  utensil: "Restaurant",
  essential: "HomeWork",

  // Personal Care & Fashion
  cosmetic: "Face",
  beauty: "Face",
  makeup: "Face",
  skincare: "Face",
  cloth: "Checkroom",
  dress: "Checkroom",
  shirt: "Checkroom",
  pant: "Checkroom",
  jewel: "Diamond",
  jewellery: "Diamond",
  gold: "Diamond",
  silver: "Diamond",
  ring: "Diamond",
  shoe: "SportsBasketball",
  footwear: "SportsBasketball",
  fashion: "Style",
  style: "Style",
  watch: "Watch",
  bag: "ShoppingBag",

  // Electronics & Tech
  phone: "PhoneAndroid",
  mobile: "PhoneAndroid",
  laptop: "Computer",
  computer: "Computer",
  electronic: "Devices",
  gadget: "Devices",
  accessory: "Cable",

  // Special Categories
  deal: "LocalOffer",
  offer: "LocalOffer",
  discount: "Discount",
  sale: "Sell",
  loot: "LocalOffer",
  new: "NewReleases",
  trending: "TrendingUp",
  popular: "Whatshot",
  best: "Star",
  top: "Star",

  // Health & Wellness
  medicine: "LocalPharmacy",
  health: "HealthAndSafety",
  vitamin: "Medication",
  fitness: "FitnessCenter",
  gym: "FitnessCenter",
  sport: "SportsSoccer",

  // Baby & Kids
  baby: "ChildCare",
  kid: "EscalatorWarning",
  toy: "ToysAndGames",

  // Pet
  pet: "Pets",
  dog: "Pets",
  cat: "Pets",
  animal: "Pets",
};

// Smart icon extraction function with keyword matching
const getAutoIcon = (categoryName) => {
  if (!categoryName) return "LocalGroceryStore";

  const lowerName = categoryName.toLowerCase().trim();

  // Step 1: Direct exact match
  if (categoryIconMap[lowerName]) {
    return categoryIconMap[lowerName];
  }

  // Step 2: Check for partial word matches (keyword extraction)
  const words = lowerName.split(/[\s&,\-_]+/);

  for (const word of words) {
    if (word.length < 3) continue;

    if (categoryIconMap[word]) {
      return categoryIconMap[word];
    }

    for (const [keyword, icon] of Object.entries(categoryIconMap)) {
      if (word.includes(keyword) || keyword.includes(word)) {
        return icon;
      }
    }
  }

  // Step 3: Check if any keyword is contained in the full name
  for (const [keyword, icon] of Object.entries(categoryIconMap)) {
    if (lowerName.includes(keyword)) {
      return icon;
    }
  }

  // Step 4: Plural handling
  const singularName = lowerName.replace(/s$/i, "");
  if (categoryIconMap[singularName]) {
    return categoryIconMap[singularName];
  }

  // Step 5: Default fallback
  return "LocalGroceryStore";
};

// Extended icon list for the dropdown
const popularIcons = [
  "LocalGroceryStore",
  "Restaurant",
  "LocalDining",
  "Icecream",
  "AcUnit",
  "LocalBar",
  "Cake",
  "Fastfood",
  "LocalPizza",
  "RamenDining",
  "LunchDining",
  "Egg",
  "Coffee",
  "LocalCafe",
  "Liquor",
  "SoupKitchen",
  "RiceBowl",
  "EmojiFoodBeverage",
  "LocalFlorist",
  "SetMeal",
  "BakeryDining",
  "Bakery",
  "Grain",
  "Grass",
  "Cookie",
  "DinnerDining",
  "LocalDrink",
  "CleaningServices",
  "Shower",
  "Bathtub",
  "LocalMall",
  "Create",
  "TempleBuddhist",
  "Home",
  "HomeWork",
  "Face",
  "Checkroom",
  "Diamond",
  "SportsBasketball",
  "Style",
  "LocalOffer",
  "Discount",
  "Opacity",
  "Apple",
  "Yard",
  "WaterDrop",
  "FreeBreakfast",
  "Soap",
  "LocalLaundryService",
  "Store",
  "Edit",
  "Description",
  "MenuBook",
  "Kitchen",
  "Watch",
  "ShoppingBag",
  "PhoneAndroid",
  "Computer",
  "Devices",
  "Cable",
  "Sell",
  "NewReleases",
  "TrendingUp",
  "Whatshot",
  "Star",
  "LocalPharmacy",
  "HealthAndSafety",
  "Medication",
  "FitnessCenter",
  "SportsSoccer",
  "ChildCare",
  "EscalatorWarning",
  "ToysAndGames",
  "Pets",
];

const EditCategory = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingIconImage, setUploadingIconImage] = useState(false);
  const [formFields, setFormFields] = useState({
    name: "",
    images: [],
    bannerImages: [],
    bannerVideos: [],
    color: "",
    icon: "",
    iconImage: "",
    themeColor: "",
  });

  const [previews, setPreviews] = useState([]);
  const [bannerPreviews, setBannerPreviews] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [iconImagePreview, setIconImagePreview] = useState("");
  const [category, setcategory] = useState([]);
  const [isImageRemove, setIsImageRemove] = useState(false);

  let { id } = useParams();

  const history = useNavigate();
  const context = useContext(MyContext);

  useEffect(() => {
    context.setProgress(20);

    // Clean up temp uploads ONLY
    deleteData("/api/imageUpload/deleteAllImages");
    deleteData("/api/bannerUpload/deleteAllImages");
    deleteData("/api/videoUpload/deleteAllVideos");

    fetchDataFromApi(`/api/category/${id}`).then((res) => {
      const categoryData = res?.categoryData[0];
      setcategory(categoryData);
      setPreviews(categoryData?.images || []);
      setBannerPreviews(categoryData?.bannerImages || []);
      setVideoPreviews(categoryData?.bannerVideos || []);
      setIconImagePreview(categoryData?.iconImage || "");
      setFormFields({
        name: categoryData?.name || "",
        color: categoryData?.color || "",
        icon: categoryData?.icon || "",
        iconImage: categoryData?.iconImage || "",
        themeColor: categoryData?.themeColor || "",
      });
      context.setProgress(100);
    });
  }, [id]);

  const changeInput = (e) => {
    const { name, value } = e.target;

    setFormFields((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // Auto-select icon when name changes
      if (name === "name") {
        const autoIcon = getAutoIcon(value);
        if (
          !prev.icon ||
          prev.icon === "LocalGroceryStore" ||
          prev.icon === getAutoIcon(prev.name)
        ) {
          updated.icon = autoIcon;
        }
      }

      return updated;
    });
  };

  const getIconComponent = (iconName) => {
    const IconComponent = MuiIcons[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  // Icon image upload
  const onChangeIconImage = async (e) => {
    try {
      const file = e.target.files[0];

      if (!file) return;

      if (
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/png" ||
        file.type === "image/webp"
      ) {
        setUploadingIconImage(true);

        // Clear previous temp uploads
        await deleteData("/api/imageUpload/deleteAllImages");

        const iconFormdata = new FormData();
        iconFormdata.append("images", file);

        uploadImage("/api/category/upload", iconFormdata).then((res) => {
          if (res && res.length > 0) {
            const uploadedImage = res[0];
            setIconImagePreview(uploadedImage);
            setFormFields((prev) => ({
              ...prev,
              iconImage: uploadedImage,
            }));

            setUploadingIconImage(false);
            context.setAlertBox({
              open: true,
              error: false,
              msg: "Icon Image Uploaded!",
            });
          }
        }).catch((error) => {
          console.error("Icon upload error:", error);
          setUploadingIconImage(false);
        });
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Please select a valid JPG or PNG image file.",
        });
      }
    } catch (error) {
      console.log(error);
      setUploadingIconImage(false);
    }
  };

  // Remove icon image
  const removeIconImage = () => {
    const userInfo = JSON.parse(localStorage.getItem("user"));
    if (userInfo?.email === "rismicmartpvtltd@gmail.com") {
      if (iconImagePreview) {
        setIsImageRemove(true);
        deleteImages(`/api/category/deleteImage?img=${iconImagePreview}`).then(
          (res) => {
            setIconImagePreview("");
            setFormFields((prev) => ({
              ...prev,
              iconImage: "",
            }));
            setIsImageRemove(false);
            context.setAlertBox({
              open: true,
              error: false,
              msg: "Icon Image Deleted!",
            });
          }
        );
      }
    } else {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Only Admin can delete Icon Image",
      });
    }
  };

  // Category thumbnail/icon images upload
  const onChangeFile = async (e) => {
    try {
      const files = e.target.files;
      setUploading(true);

      // Clear previous temp uploads
      await deleteData("/api/imageUpload/deleteAllImages");

      const formdata = new FormData();

      for (var i = 0; i < files.length; i++) {
        if (
          files[i] &&
          (files[i].type === "image/jpeg" ||
            files[i].type === "image/jpg" ||
            files[i].type === "image/png" ||
            files[i].type === "image/webp")
        ) {
          formdata.append(`images`, files[i]);
        } else {
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Please select a valid JPG or PNG image file.",
          });
          setUploading(false);
          return false;
        }
      }

      uploadImage("/api/category/upload", formdata).then((res) => {
        if (res && res.length > 0) {
          // Merge with existing previews
          setPreviews((prev) => [...prev, ...res]);

          setUploading(false);
          context.setAlertBox({
            open: true,
            error: false,
            msg: "Images Uploaded!",
          });
        }
      }).catch((error) => {
        console.error("Upload error:", error);
        setUploading(false);
      });
    } catch (error) {
      console.log(error);
      setUploading(false);
    }
  };

  // Banner images upload
  const onChangeBannerFile = async (e) => {
    try {
      const files = e.target.files;
      setUploadingBanner(true);

      // Clear previous temp uploads
      await deleteData("/api/bannerUpload/deleteAllImages");

      const formdata = new FormData();

      for (var i = 0; i < files.length; i++) {
        if (
          files[i] &&
          (files[i].type === "image/jpeg" ||
            files[i].type === "image/jpg" ||
            files[i].type === "image/png" ||
            files[i].type === "image/webp")
        ) {
          formdata.append(`images`, files[i]);
        } else {
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Please select a valid JPG or PNG image file.",
          });
          setUploadingBanner(false);
          return false;
        }
      }

      uploadImage("/api/category/banner/upload", formdata).then((res) => {
        if (res && res.length > 0) {
          // Merge with existing banners
          setBannerPreviews((prev) => [...prev, ...res]);

          setUploadingBanner(false);
          context.setAlertBox({
            open: true,
            error: false,
            msg: "Banner Images Uploaded!",
          });
        }
      }).catch((error) => {
        console.error("Banner upload error:", error);
        setUploadingBanner(false);
      });
    } catch (error) {
      console.log(error);
      setUploadingBanner(false);
    }
  };

  // Video upload
  const onChangeVideoFile = async (e) => {
    try {
      const files = e.target.files;
      setUploadingVideo(true);

      // Clear previous temp uploads
      await deleteData("/api/videoUpload/deleteAllVideos");

      const formdata = new FormData();

      for (var i = 0; i < files.length; i++) {
        if (
          files[i] &&
          (files[i].type === "video/mp4" ||
            files[i].type === "video/webm" ||
            files[i].type === "video/ogg" ||
            files[i].type === "video/quicktime")
        ) {
          formdata.append(`videos`, files[i]);
        } else {
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Please select a valid video file (MP4, WebM, OGG, MOV).",
          });
          setUploadingVideo(false);
          return false;
        }
      }

      uploadImage("/api/category/video/upload", formdata).then((res) => {
        if (res && res.length > 0) {
          // Merge with existing videos
          setVideoPreviews((prev) => [...prev, ...res]);

          setUploadingVideo(false);
          context.setAlertBox({
            open: true,
            error: false,
            msg: "Videos Uploaded!",
          });
        }
      }).catch((error) => {
        console.error("Video upload error:", error);
        setUploadingVideo(false);
      });
    } catch (error) {
      console.log(error);
      setUploadingVideo(false);
    }
  };

  const removeImg = async (indexToRemove, imgUrl, img_id) => {
    const userInfo = JSON.parse(localStorage.getItem("user"));
    if (userInfo?.email === "rismicmartpvtltd@gmail.com") {
      setIsImageRemove(true);
      deleteImages(`/api/category/deleteImage?img=${imgUrl}`).then((res) => {
        const newArray = previews.filter((item) => item !== imgUrl);
        setPreviews(newArray);
        setIsImageRemove(false);
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Image Deleted!",
        });
      }).catch((error) => {
        console.error("Delete error:", error);
        setIsImageRemove(false);
      });
    } else {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Only Admin can delete Image",
      });
    }
  };

  const removeBannerImg = async (indexToRemove, imgUrl) => {
    const userInfo = JSON.parse(localStorage.getItem("user"));
    if (userInfo?.email === "rismicmartpvtltd@gmail.com") {
      setIsImageRemove(true);
      deleteImages(`/api/category/deleteImage?img=${imgUrl}`).then((res) => {
        const newArray = bannerPreviews.filter((item) => item !== imgUrl);
        setBannerPreviews(newArray);
        setIsImageRemove(false);
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Banner Image Deleted!",
        });
      }).catch((error) => {
        console.error("Delete error:", error);
        setIsImageRemove(false);
      });
    } else {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Only Admin can delete Banner Image",
      });
    }
  };

  const removeVideo = async (indexToRemove, videoUrl) => {
    const userInfo = JSON.parse(localStorage.getItem("user"));
    if (userInfo?.email === "rismicmartpvtltd@gmail.com") {
      setIsImageRemove(true);
      deleteImages(`/api/category/deleteVideo?video=${videoUrl}`).then(
        (res) => {
          const newArray = videoPreviews.filter((item) => item !== videoUrl);
          setVideoPreviews(newArray);
          setIsImageRemove(false);
          context.setAlertBox({
            open: true,
            error: false,
            msg: "Video Deleted!",
          });
        }
      ).catch((error) => {
        console.error("Delete error:", error);
        setIsImageRemove(false);
      });
    } else {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Only Admin can delete Video",
      });
    }
  };

  const editCat = (e) => {
    e.preventDefault();

    formFields.images = previews;
    formFields.bannerImages = bannerPreviews;
    formFields.bannerVideos = videoPreviews;

    if (
      formFields.name !== "" &&
      formFields.color !== "" &&
      previews.length !== 0
    ) {
      setIsLoading(true);

      editData(`/api/category/${id}`, formFields)
        .then((res) => {
          setIsLoading(false);
          context.fetchCategory();

          // Clean up temp uploads only
          deleteData("/api/imageUpload/deleteAllImages");
          deleteData("/api/bannerUpload/deleteAllImages");
          deleteData("/api/videoUpload/deleteAllVideos");

          context.setAlertBox({
            open: true,
            error: false,
            msg: "Category Updated Successfully!",
          });

          history("/category");
        })
        .catch((error) => {
          console.error("Update error:", error);
          setIsLoading(false);
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Failed to update category!",
          });
        });
    } else {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please fill all the details",
      });
      return false;
    }
  };

  return (
    <>
      <div className="right-content w-100">
        <div className="card shadow border-0 w-100 flex-row p-4 mt-2">
          <h5 className="mb-0">Edit Category</h5>
          <Breadcrumbs aria-label="breadcrumb" className="ml-auto breadcrumbs_">
            <StyledBreadcrumb
              component="a"
              href="#"
              label="Dashboard"
              icon={<HomeIcon fontSize="small" />}
            />

            <StyledBreadcrumb
              component="a"
              label="Category"
              href="#"
              deleteIcon={<ExpandMoreIcon />}
            />
            <StyledBreadcrumb
              label="Edit Category"
              deleteIcon={<ExpandMoreIcon />}
            />
          </Breadcrumbs>
        </div>

        <form className="form" onSubmit={editCat}>
          <div className="row">
            <div className="col-sm-9">
              <div className="card p-4 mt-0">
                <div className="form-group">
                  <h6>Category Name</h6>
                  <input
                    type="text"
                    name="name"
                    value={formFields.name}
                    onChange={changeInput}
                    placeholder="e.g., Vegetables & Fruits, Dairy & Breads, Baby Products..."
                  />
                  {formFields.name && (
                    <small
                      style={{
                        color: "#2196f3",
                        marginTop: "5px",
                        display: "block",
                      }}
                    >
                      {getAutoIcon(formFields.name) !== formFields.icon
                        ? `💡 Suggested icon: ${getAutoIcon(formFields.name)}`
                        : `✨ Current icon: ${formFields.icon}`}
                    </small>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <h6>Card Background Color</h6>
                      <input
                        type="color"
                        name="color"
                        value={formFields.color}
                        onChange={changeInput}
                        style={{ width: "100%", height: "40px" }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <h6>Theme Color (Product Listing Page)</h6>
                      <input
                        type="color"
                        name="themeColor"
                        value={formFields.themeColor}
                        onChange={changeInput}
                        style={{ width: "100%", height: "40px" }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <h6>
                    Category Icon (Auto-Selected / Manual Override / Upload
                    Image)
                  </h6>

                  <FormControl fullWidth style={{ marginBottom: "15px" }}>
                    <InputLabel>Select Material-UI Icon</InputLabel>
                    <Select
                      name="icon"
                      value={formFields.icon}
                      onChange={changeInput}
                      label="Select Material-UI Icon"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {popularIcons.map((iconName) => (
                        <MenuItem key={iconName} value={iconName}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            {getIconComponent(iconName)}
                            <span>{iconName}</span>
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "15px 0",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        background: "#ddd",
                      }}
                    ></div>
                    <span
                      style={{
                        padding: "0 10px",
                        color: "#999",
                        fontSize: "14px",
                      }}
                    >
                      OR
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        background: "#ddd",
                      }}
                    ></div>
                  </div>

                  <div
                    style={{
                      border: "2px dashed #ddd",
                      borderRadius: "8px",
                      padding: "20px",
                      textAlign: "center",
                      background: "#fafafa",
                    }}
                  >
                    {iconImagePreview ? (
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <img
                          src={iconImagePreview}
                          alt="Icon"
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            border: "2px solid #2196f3",
                          }}
                        />
                        <span
                          onClick={removeIconImage}
                          style={{
                            position: "absolute",
                            top: "-10px",
                            right: "-10px",
                            background: "#f44336",
                            color: "white",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "16px",
                          }}
                        >
                          <IoCloseSharp />
                        </span>
                      </div>
                    ) : uploadingIconImage ? (
                      <div>
                        <CircularProgress size={40} />
                        <p style={{ marginTop: "10px", color: "#666" }}>
                          Uploading Icon Image...
                        </p>
                      </div>
                    ) : (
                      <label
                        htmlFor="iconImageUpload"
                        style={{
                          cursor: "pointer",
                          display: "block",
                        }}
                      >
                        <FaRegImages
                          style={{ fontSize: "40px", color: "#2196f3" }}
                        />
                        <p style={{ marginTop: "10px", color: "#666" }}>
                          Upload Custom Icon Image
                        </p>
                        <input
                          id="iconImageUpload"
                          type="file"
                          accept="image/*"
                          onChange={onChangeIconImage}
                          style={{ display: "none" }}
                        />
                      </label>
                    )}
                  </div>

                  {(formFields.icon || iconImagePreview) && (
                    <div
                      style={{
                        marginTop: "15px",
                        padding: "15px",
                        background: formFields.color || "#f5f5f5",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ fontWeight: "600" }}>Preview:</span>
                      {iconImagePreview ? (
                        <img
                          src={iconImagePreview}
                          alt="Icon Preview"
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        getIconComponent(formFields.icon)
                      )}
                      <span style={{ color: "#666", fontSize: "14px" }}>
                        {iconImagePreview
                          ? "Custom Image Icon"
                          : formFields.icon}
                      </span>
                    </div>
                  )}
                </div>

                <div className="imagesUploadSec">
                  <h5 className="mb-4">Category Icon/Thumbnail Image</h5>

                  <div className="imgUploadBox d-flex align-items-center">
                    {previews?.length !== 0 &&
                      previews?.map((img, index) => {
                        return (
                          <div className="uploadBox" key={index}>
                            <span
                              className="remove"
                              disabled={isImageRemove === true}
                              onClick={() => removeImg(index, img, img?.id)}
                            >
                              <IoCloseSharp />
                            </span>
                            <div className="box">
                              <LazyLoadImage
                                alt={"image"}
                                effect="blur"
                                className="w-100"
                                src={img}
                              />
                            </div>
                          </div>
                        );
                      })}

                    <div className="uploadBox">
                      {uploading === true ? (
                        <div className="progressBar text-center d-flex align-items-center justify-content-center flex-column">
                          <CircularProgress />
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            multiple
                            onChange={(e) => onChangeFile(e)}
                            name="images"
                          />
                          <div className="info">
                            <FaRegImages />
                            <h5>image upload</h5>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="imagesUploadSec mt-4">
                  <h5 className="mb-4">
                    Product Listing Page Banners (Images)
                  </h5>

                  <div className="imgUploadBox d-flex align-items-center">
                    {bannerPreviews?.length !== 0 &&
                      bannerPreviews?.map((img, index) => {
                        return (
                          <div className="uploadBox" key={index}>
                            <span
                              className="remove"
                              disabled={isImageRemove === true}
                              onClick={() => removeBannerImg(index, img)}
                            >
                              <IoCloseSharp />
                            </span>
                            <div className="box">
                              <LazyLoadImage
                                alt={"banner"}
                                effect="blur"
                                className="w-100"
                                src={img}
                              />
                            </div>
                          </div>
                        );
                      })}

                    <div className="uploadBox">
                      {uploadingBanner === true ? (
                        <div className="progressBar text-center d-flex align-items-center justify-content-center flex-column">
                          <CircularProgress />
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            multiple
                            onChange={(e) => onChangeBannerFile(e)}
                            name="bannerImages"
                          />
                          <div className="info">
                            <FaRegImages />
                            <h5>banner upload</h5>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="imagesUploadSec mt-4">
                  <h5 className="mb-4">Product Listing Page Videos</h5>

                  <div className="imgUploadBox d-flex align-items-center">
                    {videoPreviews?.length !== 0 &&
                      videoPreviews?.map((vid, index) => {
                        return (
                          <div className="uploadBox" key={index}>
                            <span
                              className="remove"
                              disabled={isImageRemove === true}
                              onClick={() => removeVideo(index, vid)}
                            >
                              <IoCloseSharp />
                            </span>
                            <div className="box">
                              <video
                                controls
                                className="w-100"
                                src={vid}
                                style={{ maxHeight: "200px" }}
                              />
                            </div>
                          </div>
                        );
                      })}

                    <div className="uploadBox">
                      {uploadingVideo === true ? (
                        <div className="progressBar text-center d-flex align-items-center justify-content-center flex-column">
                          <CircularProgress />
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            multiple
                            accept="video/*"
                            onChange={(e) => onChangeVideoFile(e)}
                            name="bannerVideos"
                          />
                          <div className="info">
                            <FaRegImages />
                            <h5>video upload</h5>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <br />

                <Button type="submit" className="btn-blue btn-lg btn-big w-100">
                  <FaCloudUploadAlt /> &nbsp;{" "}
                  {isLoading === true ? (
                    <CircularProgress color="inherit" className="loader" />
                  ) : (
                    "UPDATE AND VIEW"
                  )}{" "}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditCategory;

